import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, LocateFixed, MapPin, Navigation } from "lucide-react";
import { MAPPED_STORES, km, fmtKm, directionsUrl } from "@/lib/stores";
import { Reveal, Eyebrow } from "./Reveal";

const CHAIN_NAMES = ["Sobeys", "Lawtons", "Harvest Clean Eats", "Popeye's", "Simply For Life"];

const NearCard = ({ s, i }) => (
  <Reveal delay={i * 0.08} data-testid={`nearby-store-${i}`} className="rounded-[24px] bg-white text-[#1B1530] p-6 flex flex-col">
    <div className="flex items-center justify-between gap-3">
      <MapPin className="h-5 w-5 text-[#FF5A5F]" />
      <span className="rounded-full bg-[#FFB21A] px-3 py-1 text-xs font-bold">{fmtKm(s.dist)}</span>
    </div>
    <p className="mt-4 font-display text-2xl leading-tight">{s.name}</p>
    <p className="mt-1 text-sm text-[#4A4560]">{s.address}</p>
    <a href={directionsUrl(s)} target="_blank" rel="noreferrer" data-testid={`nearby-store-directions-${i}`}
      className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#FF5A5F] hover:underline"><Navigation className="h-4 w-4" /> Directions</a>
  </Reveal>
);

export const NearbyStores = () => {
  const [state, setState] = useState("idle");
  const [near, setNear] = useState([]);

  const locate = () => {
    if (!navigator.geolocation) { setState("error"); return; }
    setState("loading");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const me = { lat: p.coords.latitude, lng: p.coords.longitude };
        setNear(MAPPED_STORES.map((s) => ({ ...s, dist: km(me, s) })).sort((a, b) => a.dist - b.dist).slice(0, 3));
        setState("done");
      },
      () => setState("error"),
      { timeout: 10000 },
    );
  };

  return (
    <section data-testid="home-nearby-stores" className="py-20 lg:py-24 bg-[#1B1530] text-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-5">
          <Eyebrow className="text-[#FFB21A] mb-4">Find us nearby</Eyebrow>
          <h2 className="font-display text-4xl sm:text-6xl leading-[0.95]">Grab a bag on your way home.</h2>
          <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed">In {MAPPED_STORES.length}+ spots across Atlantic Canada, Ontario and Quebec, including {CHAIN_NAMES.join(", ")} and local gyms.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={locate} data-testid="nearby-locate-button" disabled={state === "loading"}
              className="h-14 px-7 rounded-full bg-[#FF5A5F] font-bold inline-flex items-center gap-2 hover:bg-white hover:text-[#1B1530] transition-colors disabled:opacity-70">
              {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
              {state === "done" ? "Refresh my location" : "Show stores near me"}
            </button>
            <Link to="/stores" data-testid="nearby-all-stores-link" className="h-14 px-7 rounded-full border-2 border-white/40 font-bold inline-flex items-center gap-2 hover:bg-white hover:text-[#1B1530] transition-colors">
              See the map <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          {state === "error" && <p data-testid="nearby-error" className="mt-4 text-sm text-white/80">We couldn't get your location. Try the map and search your town instead.</p>}
        </Reveal>
        <div className="lg:col-span-7">
          {state === "done" ? (
            <div className="grid sm:grid-cols-3 gap-4" data-testid="nearby-results">
              {near.map((s, i) => <NearCard key={s.id} s={s} i={i} />)}
            </div>
          ) : (
            <Reveal className="relative rounded-[28px] overflow-hidden aspect-[16/10]">
              <img src="/img/store-display.jpg" alt="Swole Bears display in a store" loading="lazy" className="h-full w-full object-cover" />
              <span className="absolute bottom-4 left-4 rounded-full bg-white text-[#1B1530] text-sm font-bold px-4 py-2">On shelves at {MAPPED_STORES.length}+ locations</span>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};
